// Helper functions for bill printing
export const formatDate = (dateString) => {
  if (!dateString) return "";
  // Remove leading quote if present (from your data format '11/12/2025)
  const cleanDate = dateString.replace(/^'/, "");
  return cleanDate;
};

export const generateTransactionBillHTML = (transaction, customers) => {
  const customer = customers.find((c) => c.id === transaction.customer) || {};
  const isGst = transaction.transactionDetails?.gstApplicable;

  // Use the existing generateBillHTML function or create a new one
  const billData = {
    type: isGst ? "gst" : "non-gst",
    data: {
      transactionDetails: {
        billNo: transaction.transactionDetails?.billNo || 1,
        gstApplicable: isGst,
      },
      date: transaction.date,
      calculatedTotals: {
        subtotal: transaction.calculatedTotals?.subtotal || 0,
      },
      paymentDetails: {
        cash: transaction.paymentDetails?.cash || 0,
        online: transaction.paymentDetails?.online || 0,
        cheque: transaction.paymentDetails?.cheque || 0,
        other: transaction.paymentDetails?.other || 0,
        advancePayment: {
          enabled: transaction.paymentDetails?.advancePayment?.enabled || false,
          total: transaction.paymentDetails?.advancePayment?.total || 0,
          breakup: transaction.paymentDetails?.advancePayment?.breakup || {},
        },
        discount: transaction.paymentDetails?.discount || 0,
      },
      metaDetails: {
        handledBy: transaction.metaDetails?.handledBy || "",
        gstNumber: transaction.metaDetails?.gstNumber || "",
        panNumber: transaction.metaDetails?.panNumber || "",
      },
      summary: {
        grandTotal: transaction.summary?.grandTotal || 0,
        pendingAmount: transaction.summary?.pendingAmount || 0,
        cgst: transaction.summary?.cgst || 0,
        sgst: transaction.summary?.sgst || 0,
      },
    },
    items: transaction.items || [],
    payments: transaction.paymentDetails || {},
    customer: customer,
    advanceData: transaction.paymentDetails?.advancePayment?.breakup || {},
  };
  console.log("Generated Bill Data:", billData); // Debug log to check the structure of billData
  return generateBillHTML(billData);
};

export const parseItemObject = (itemObject) => {
  if (!itemObject) return {};
  try {
    if (typeof itemObject === "string") {
      return JSON.parse(decodeURIComponent(itemObject));
    }
    return itemObject;
  } catch (error) {
    console.warn("Could not parse item object:", error);
    return {};
  }
};

export const getCarat = (melting) => {
  if (!melting) return "";
  switch (String(melting)) {
    case "92":
      return "22ct";
    case "75":
      return "18ct";
    case "76":
      return "18ct";
    case "58.5":
      return "14ct";
    case "50":
      return "12ct";
    case "999":
      return "24ct";
    default:
      return "";
  }
};

// Indian Number to Words Conversion
export const numberToWords = (num) => {
  if (typeof num !== "number") num = parseFloat(num) || 0;
  num = Math.floor(num); // Convert to integer

  if (num === 0) return "Zero Rupees Only";

  const ones = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const tens = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const convertLessThanOneThousand = (n) => {
    if (n === 0) return "";

    let result = "";

    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + " Hundred ";
      n %= 100;
    }

    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + " ";
      n %= 10;
    }

    if (n > 0) {
      result += ones[n] + " ";
    }

    return result.trim();
  };

  const convertToIndianWords = (n) => {
    if (n === 0) return "";

    let crore = Math.floor(n / 10000000);
    n %= 10000000;

    let lakh = Math.floor(n / 100000);
    n %= 100000;

    let thousand = Math.floor(n / 1000);
    n %= 1000;

    let hundred = n;

    let result = "";

    if (crore > 0) {
      result += convertLessThanOneThousand(crore) + " Crore ";
    }

    if (lakh > 0) {
      result += convertLessThanOneThousand(lakh) + " Lakh ";
    }

    if (thousand > 0) {
      result += convertLessThanOneThousand(thousand) + " Thousand ";
    }

    if (hundred > 0) {
      result += convertLessThanOneThousand(hundred);
    }

    return result.trim();
  };

  const words = convertToIndianWords(num);
  return words ? words + " Rupees Only" : "Zero Rupees Only";
};

// Generate Non-GST Bill HTML - Premium Minimal Design with Border
const generateNonGSTBill = (data, items, payments, customer, advanceData) => {
  let itemsHTML = "";
  let total = 0;
  const isGst = data.transactionDetails?.gstApplicable || false;

  items.forEach((item, index) => {
    // const itemObj = parseItemObject(item.itemObject);
    const amount = parseFloat(item.total) || 0;
    total += amount;
    itemsHTML += `
       <tr>
        <td style="padding: 6px 6px; text-align: left; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 30%;">${item.name
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.weight || "0.000"
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.ct || ""
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.metalRate
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.wastage || item.labour || 0
      }%</td>
        <td style="padding: 6px 6px; text-align: right; border-bottom: 1px solid #333; font-weight: 500; color: #2c3e50; font-size: 11px; width: 22%;">₹${amount.toFixed(
        2
      )}</td>
       </tr>
    `;
  });

  const discount = parseFloat(payments?.discount || 0);
  const grandTotal = total - discount;

  const currentRowCount = items.length;
  const minTotalRows = 10;
  const rowsNeeded = minTotalRows - (currentRowCount + 2);

  if (rowsNeeded > 0) {
    for (let i = 0; i < rowsNeeded; i++) {
      itemsHTML += `
          <tr style="max-height: 26px;">
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333;">&nbsp;</td>
           </tr>`;
    }
  }

  itemsHTML += `
    <tr>
      <td colspan="5" style="padding: 6px 6px; text-align: right; background-color: #f8f9fa; border-top: 3px solid #333; border-right: 1px solid #333; font-size: 12px; border-bottom: 1px solid #333;;"><strong>Subtotal</strong></td>
      <td style="padding: 6px 6px; text-align: right; background-color: #f8f9fa; border-top: 3px solid #333; border-bottom: 1px solid #333; font-weight: 500; font-size: 12px;">₹${total.toFixed(
    2
  )}</td>
    </tr>
    <tr>
      <td colspan="5" style="padding: 6px 6px; text-align: right; border-right: 1px solid #333; border-bottom: 3px solid #333; border-right: 1px solid #333; font-size: 12px;"><span style="color: #666;">Discount</span></td>
      <td style="padding: 6px 6px; text-align: right; font-weight: 500; font-size: 12px; color: #d32f2f; border-bottom: 3px solid #333;">-₹${discount.toFixed(
    2
  )}</td>
    </tr>
    <tr>
      <td colspan="5" style="padding: 12px 6px; text-align: right; font-size: 14px; background-color: #f8f9fa; border-bottom: 3px solid #333; border-right: 1px solid #333;"><strong style="color: #2c3e50;">GRAND TOTAL</strong></td>
      <td style="padding: 12px 6px; text-align: right; font-size: 16px; font-weight: bold; background-color: #f8f9fa; border-bottom: 3px solid #333; color: #000000; font-size: 12px;">₹${grandTotal.toFixed(
    2
  )}</td>
    </tr>`;

  const totalReceived =
    (parseFloat(payments?.cash) || 0) +
    (parseFloat(payments?.online) || 0) +
    (parseFloat(payments?.cheque) || 0) +
    (parseFloat(payments?.other) || 0) +
    (parseFloat(payments?.advancePayment?.total) || 0);

  const chargeableAmt =
    totalReceived + (parseFloat(data?.summary?.pendingAmount) || 0);

  return `
    <div id="billContentWithoutGst" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; width: 750px; margin: 0 auto; background: white; box-shadow: 0 10px 30px rgba(0,0,0,0.1); padding: 0; box-sizing: border-box; position: relative; border: 2px solid #2c3e50;">
      
      <!-- Hindi Blessings at Top -->
      <div style="text-align: center; padding: 8px 0; background: #f0f2f5; border-bottom: 2px solid #c4a747; print-color-adjust: exact; -webkit-print-color-adjust: exact;">
        <div style="font-size: 10px; font-weight: 500; color: #2c3e50; font-family: 'Arial', sans-serif;">
          <span style="margin: 0 25px;">|| खेतपाल बावजी नमः ||</span>
          <span style="margin: 0 25px;">|| श्री गणेशाय नमः ||</span>
        </div>
      </div>
      
      <!-- Premium Header with Gold Accent -->
      <div style="background: linear-gradient(135deg, #2c3e50 0%, #1e2b37 100%); padding: 2px 25px; color: white; border-bottom: 3px solid #d4af37;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div style="display: flex; align-items: center; gap: 15px;">
            <img 
              src="assets/images/logo.png" 
              alt="Logo" 
              style="width: 50px; height: 50px; border-radius: 50%; object-fit: cover; box-shadow: 0 2px 10px rgba(0,0,0,0.2);"
            />
            <div>
              <h1 style="font-family: 'Playfair Display', 'Times New Roman', serif; font-size: 32px; margin: 0; font-weight: 600; letter-spacing: 1px; color: #d4af37;">ANKITA JEWELLERS</h1>
              <p style="margin: 5px 0 0 0; font-size: 13px; opacity: 0.9; letter-spacing: 1px;">EXCLUSIVE 916 HALLMARK GOLD SHOWROOM</p>
            </div>
          </div>
          <div style="text-align: right;">
            <p style="margin: 3px 0 0 0; font-size: 12px;">📞 98672 68280</p>
            <p style="margin: 3px 0 0 0; font-size: 12px;">📞 73041 92892</p>
          </div>
        </div>
      </div>
      
      <!-- Address Bar -->
      <div style="background-color: #f8f9fa; padding: 8px 25px; border-bottom: 1px solid #e0e0e0; font-size: 12px; color: #2c3e50; text-align: center;">
        Shop No. 11, Panch Bhagini Sadan, B. P. Road, Bhayandar(East), Dist. Thane - 401105
      </div>
      
      <!-- Tax Invoice / Cash Memo -->
      <div style="text-align: center; padding: 10px 0; background-color: white;">
        <span style="font-size: 20px; font-weight: 700; color: #2c3e50; letter-spacing: 2px;">${isGst ? "TAX INVOICE" : "CASH MEMO"
    }</span>
      </div>
      
      <!-- Customer & Bill Info - Minimal Grid -->
      <div style="padding: 5px 25px; display: grid; grid-template-columns: 1fr 1fr; gap: 0px; border-bottom: 1px solid #e0e0e0;">
        <div>
          <p style="margin: 5px 0; font-size: 14px; color: #2c3e50;">Name:<strong> ${customer?.name || ""
    }</strong></p>
          <p style="margin: 5px 0; font-size: 13px; color: #666;">Address: ${customer?.address || ""
    }</p>
          <p style="margin: 5px 0; font-size: 13px; color: #666;">Mob. No. ${customer?.contact || ""
    }</p>
          ${data.metaDetails?.gstNumber
      ? `<p style="margin: 5px 0; font-size: 12px; color: #666;">GST: ${data.metaDetails.gstNumber}</p>`
      : ""
    }
        </div>
        <div style="text-align: right;">
          <p style="margin: 5px 0; font-size: 14px;"><span style="color: #666;">Bill No:</span> <strong style="color: #2c3e50;">${data.transactionDetails?.billNo || 1
    }</strong></p>
          <p style="margin: 5px 0; font-size: 14px;"><span style="color: #666;">Date:</span> <strong style="color: #2c3e50;">${formatDate(
      data.date
    )}</strong></p>
          <p style="margin: 5px 0; font-size: 14px;"><span style="color: #666;">Seller:</span> <strong style="color: #2c3e50;">${data.metaDetails?.handledBy || ""
    }</strong></p>
          ${data.metaDetails?.panNumber
      ? `<p style="margin: 5px 0; font-size: 12px; color: #666;">PAN: ${data.metaDetails.panNumber}</p>`
      : ""
    }
        </div>
      </div>
      
      <!-- Items Table - Clean Design with Vertical Lines -->
      <div style="padding-bottom: 10px;">
        <table style="width: 100%; border-collapse: collapse;">
          <thead>
  <tr style="background-color: #f8f9fa; border-bottom: 3px solid #333; border-top: 3px solid #333;">
    <th style="padding: 6px 6px; text-align: left; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333;  width: 40%;">Particular</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">Net. Wt</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">CT.</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">Rate</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">Making(%)</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; width: 22%;">Amount</th>
  </tr>
</thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
      </div>

      <!-- Amount in Words - Premium Highlight -->
      <div style="margin: 0 5px 5px 5px; background-color: #2c3e50; padding: 10px 20px; border-radius: 4px; color: white; border: 1px solid #d4af37;">
        <div style="display: flex; align-items: center;">
          <span style="font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #d4af37; margin-right: 15px;">Amount in Words</span>
          <span style="font-size: 14px; font-weight: 500;">${numberToWords(
      chargeableAmt
    ).toUpperCase()}</span>
        </div>
      </div>
      
      <!-- Payment Details Table -->
      <div style="padding: 0 5px 5px 5px;">
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #e0e0e0;">
          <thead>
            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #9b8c7c;">
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Cash</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Online</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Cheque</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Old Gold</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Advance</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50;">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid #e0e0e0;">
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.cash || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.online || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.cheque || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.other || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.advancePayment?.total || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 600; color: #ff0000;">₹${parseFloat(
      data?.summary?.pendingAmount || 0
    ).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      ${payments?.advancePayment?.enabled
      ? `
        <div style="padding: 0 5px 5px 5px;">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e0e0e0;">
            <thead>
              <tr style="background-color: #f8f9fa; border-bottom: 2px solid #9b8c7c;">
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Cash</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Online</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Gold</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Silver</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Other</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50;">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid #e0e0e0;">
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.cash || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.online || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.oldGold || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.oldSilver || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.custom || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; font-weight: 600; color: #9b8c7c; font-size: 12px;">₹${payments?.advancePayment?.total || 0
      }</td>
              </tr>
            </tbody>
          </table>
        </div>
      `
      : ""
    }
      
      <!-- HSN & Bank Details with QR Code -->
      <div style="padding: 0 5px 5px 5px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; border: 1px solid #e0e0e0;">
        
          <p style="margin: 8px 0 0 0; font-size: 11px; font-weight: 600; color: #2c3e50; text-transform: uppercase;">Terms & Conditions:</p>
          <ul style="margin: 0; padding-left: 15px; font-size: 10px; color: #666; line-height: 1.5;">
            <li>20% Deduction in KDM Ornaments.</li>
            <li>9% Deduction in Cash Transaction for 916 Hallmark Jewellery.</li>
            <li>Please verify item details at the time of delivery.</li>
            <li style="font-weight: bold; font-size: 15px; list-style-type: none; margin: 10px 0;">THANK YOU VISIT AGAIN</li>
          </ul>
        </div>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; border: 1px solid #e0e0e0; display: flex; align-items: center; justify-content: space-between;">
          <div>
            <p style="margin: 0 0 5px 0; font-size: 12px; color: #666;">Scan to Pay</p>
            <p style="margin: 2px 0; font-size: 11px; color: #2c3e50;"><strong>HDFC BANK</strong></p>
            <p style="margin: 2px 0; font-size: 10px; color: #666;">A/C: 50200045267402</p>
            <p style="margin: 2px 0; font-size: 10px; color: #666;">IFSC: HDFC0001116</p>
          </div>
          <div style="text-align: center;">
            <img src="/assets/images/scanner.jpeg" style="width: 80px; height: 80px; border: 1px solid #e0e0e0; padding: 5px; background: white;">
            <p style="margin: 5px 0 0 0; font-size: 8px; color: #666;">Scan with any UPI app</p>
          </div>
        </div>
      </div>
      
      <!-- Declaration, Terms & Signatures with Social Media -->
      <div style="padding: 0 5px 5px 5px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          <div style="border: 1px solid #e0e0e0; padding: 15px; border-radius: 4px; background-color: #f8f9fa;">
            <p style="margin: 0 0 5px 0; font-size: 11px; color: #666;">Customer's Signature</p>
            <div style="height: 40px; border-bottom: 1px dashed #9b8c7c; margin-bottom: 10px;"></div>
          </div>
          <div style="text-align: right;">
            <div style="border: 1px solid #e0e0e0; padding: 16px; border-radius: 4px; background-color: #f8f9fa;">
              <p style="margin: 0; font-size: 11px; color: #666;">For ANKITA JEWELLERS</p>
              <div style="margin-top: 40px; border-top: 1px dashed #9b8c7c; padding-top: 10px;"></div>
            </div>
          </div>
        </div>
        
       <!-- Social Media Section with SVG Icons -->
<div style="margin-top: 10px; padding: 10px 15px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-radius: 4px; border: 1px solid #d4af37; text-align: center;">
  <div style="display: flex; justify-content: center; align-items: center; gap: 30px;">
    <a href="https://www.instagram.com/ankitajwls?utm_source=qr&igsh=MXQwNHNjcHo3dTZocQ%3D%3D" target="_blank" style="text-decoration: none; display: flex; align-items: center; gap: 8px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E4405F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
      </svg>
      <span style="font-size: 11px; font-weight: 500; color: #2c3e50;">@ANKITAJWLS</span>
    </a>
    <div style="width: 1px; height: 20px; background-color: #d4af37;"></div>
    <a href="https://wa.me/917304192892" target="_blank" style="text-decoration: none; display: flex; align-items: center; gap: 8px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
      </svg>
      <span style="font-size: 11px; font-weight: 500; color: #2c3e50;">+91 73041 92892</span>
    </a>
  </div>
</div>
      
      <!-- Watermark -->
      <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.15; z-index: 9999; pointer-events: none;">
        <img src="/assets/images/logo.png" alt="Logo" style="width: 500px; height: 500px; object-fit: contain;">
      </div>
    </div>
  `;
};

// Generate GST Bill HTML - Premium Minimal Design with Border
const generateGSTBill = (data, items, payments, customer, advanceData) => {
  let itemsHTML = "";
  let total = 0;
  const isGst = data.transactionDetails?.gstApplicable || true;

  items.forEach((item, index) => {
    const itemObj = parseItemObject(item.itemObject);
    console.log("Parsed Item Object:", itemObj);
    const amount = parseFloat(item.total) || 0;
    total += amount;

    itemsHTML += `
       <tr>
        <td style="padding: 6px 6px; text-align: left; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 30%;">${item.name
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item?.weight
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.ct || ""
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.metalRate
      }</td>
        <td style="padding: 6px 6px; text-align: center; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 11px; width: 12%;">${item.wastage || item.labour
      || 0}%</td>
        <td style="padding: 6px 6px; text-align: right; border-bottom: 1px solid #333; font-weight: 500; color: #2c3e50; font-size: 11px; width: 22%;">₹${amount.toFixed(
        2
      )}</td>
       </tr>
    `;
  });

  // Calculate taxes
  const cgst = total * 0.015;
  const sgst = total * 0.015;
  const discount = parseFloat(payments?.discount || 0);
  const subtotalAfterTax = total + cgst + sgst;
  const grandTotal = subtotalAfterTax - discount;

  // Calculate filler rows
  const currentRowCount = items.length;
  const minTotalRows = 10;
  const rowsNeeded = minTotalRows - (currentRowCount + 5); // +5 for tax rows + discount

  if (rowsNeeded > 0) {
    for (let i = 0; i < rowsNeeded; i++) {
      itemsHTML += `
          <tr style="max-height: 26px;">
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333; border-right: 1px solid #333;">&nbsp;</td>
            <td style="border-bottom: 1px solid #333;">&nbsp;</td>
           </tr>`;
    }
  }

  itemsHTML += `
     <tr>
      <td colspan="5" style="padding: 6px 6px; text-align: right; background-color: #f8f9fa; border-top: 3px solid #333; border-right: 1px solid #333; font-size: 12px; border-bottom: 2px solid #333; border-right: 2px solid #333;"><strong>Subtotal</strong></td>
      <td style="padding: 6px 6px; text-align: right; background-color: #f8f9fa; border-top: 3px solid #333; font-weight: 500; border-bottom: 2px solid #333; font-size: 12px;">₹${total.toFixed(
    2
  )}</td>
     </tr>
     <tr>
      <td colspan="5" style="padding: 6px 6px; text-align: right; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 12px;"><span style="color: #666;">CGST (1.5%)</span></td>
      <td style="padding: 6px 6px; text-align: right; font-weight: 500; font-size: 12px;  border-bottom: 1px solid #333;">₹${cgst.toFixed(
    2
  )}</td>
     </tr>
     <tr>
      <td colspan="5" style="padding: 6px 6px; text-align: right; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 12px;"><span style="color: #666;">SGST (1.5%)</span></td>
      <td style="padding: 6px 6px; text-align: right; font-weight: 500; font-size: 12px; border-bottom: 1px solid #333; ">₹${sgst.toFixed(
    2
  )}</td>
     </tr>
     <tr>
      <td colspan="5" style="padding: 6px 6px; text-align: right; border-bottom: 1px solid #333; border-right: 1px solid #333; font-size: 12px;"><span style="color: #666;">Discount</span></td>
      <td style="padding: 6px 6px; text-align: right; font-weight: 500; font-size: 12px; color: #d32f2f; border-bottom: 1px solid #333;">-₹${discount.toFixed(
    2
  )}</td>
     </tr>
     <tr>
      <td colspan="5" style="padding: 12px 6px; text-align: right; font-size: 14px; background-color: #f8f9fa; border-top: 3px solid #333; border-bottom: 3px solid #333; border-right: 1px solid #333;"><strong style="color: #2c3e50;">GRAND TOTAL</strong></td>
      <td style="padding: 12px 6px; text-align: right; font-size: 16px; font-weight: bold; background-color: #f8f9fa; border-top: 3px solid #333; border-bottom: 3px solid #333; color: #000000; font-size: 12px;">₹${grandTotal.toFixed(
    2
  )}</td>
     </tr>`;

  const totalReceived =
    (parseFloat(payments?.cash) || 0) +
    (parseFloat(payments?.online) || 0) +
    (parseFloat(payments?.cheque) || 0) +
    (parseFloat(payments?.other) || 0) +
    (parseFloat(payments?.advancePayment?.total) || 0);

  const chargeableAmt =
    totalReceived + (parseFloat(data?.summary?.pendingAmount) || 0);

  return `
    <div id="billContent" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; width: 750px; margin: 0 auto; background: white; box-shadow: 0 10px 30px rgba(0,0,0,0.1); padding: 0; box-sizing: border-box; position: relative; border: 2px solid #2c3e50;">
      
      <!-- Hindi Blessings at Top -->
      <div style="text-align: center; padding: 8px 0; background: #f0f2f5; border-bottom: 2px solid #c4a747; print-color-adjust: exact; -webkit-print-color-adjust: exact;">
        <div style="font-size: 10px; font-weight: 500; color: #2c3e50; font-family: 'Arial', sans-serif;">
          <span style="margin: 0 25px;">|| खेतपाल बावजी नमः ||</span>
          <span style="margin: 0 25px;">|| श्री गणेशाय नमः ||</span>
        </div>
      </div>
      
      <!-- Premium Header with Gold Accent -->
      <div style="background: linear-gradient(135deg, #2c3e50 0%, #1e2b37 100%); padding: 2px 25px; color: white; border-bottom: 3px solid #d4af37;">
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <div style="display: flex; align-items: center; gap: 15px;">
            <img 
              src="assets/images/logo.png" 
              alt="Logo" 
              style="width: 50px; height: 50px; border-radius: 50%; object-fit: cover; box-shadow: 0 2px 10px rgba(0,0,0,0.2);"
            />
            <div>
              <h1 style="font-family: 'Playfair Display', 'Times New Roman', serif; font-size: 32px; margin: 0; font-weight: 600; letter-spacing: 1px; color: #d4af37;">ANKITA JEWELLERS</h1>
              <p style="margin: 5px 0 0 0; font-size: 13px; opacity: 0.9; letter-spacing: 1px;">EXCLUSIVE 916 HALLMARK GOLD SHOWROOM</p>
            </div>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; font-size: 12px; color: #d4af37;">GST: 27ABDPJ0478P1Z8</p>
            <p style="margin: 3px 0 0 0; font-size: 12px;">📞 98672 68280</p>
            <p style="margin: 3px 0 0 0; font-size: 12px;">📞 73041 92892</p>
          </div>
        </div>
      </div>
      
      <!-- Address Bar -->
      <div style="background-color: #f8f9fa; padding: 8px 25px; border-bottom: 1px solid #e0e0e0; font-size: 12px; color: #2c3e50; text-align: center;">
        Shop No. 11, Panch Bhagini Sadan, B. P. Road, Bhayandar(East), Dist. Thane - 401105
      </div>
      
      <!-- Tax Invoice / Cash Memo -->
      <div style="text-align: center; padding: 10px 0; background-color: white;">
        <span style="font-size: 20px; font-weight: 700; color: #2c3e50; letter-spacing: 2px;">${isGst ? "TAX INVOICE" : "CASH MEMO"
    }</span>
      </div>
      
      <!-- Customer & Bill Info - Minimal Grid -->
      <div style="padding: 5px 25px; display: grid; grid-template-columns: 1fr 1fr; gap: 0px; border-bottom: 1px solid #e0e0e0;">
        <div>
          <p style="margin: 5px 0; font-size: 14px; color: #2c3e50;">Name:<strong> ${customer?.name || ""
    }</strong></p>
          <p style="margin: 5px 0; font-size: 13px; color: #666;">Address: ${customer?.address || ""
    }</p>
          <p style="margin: 5px 0; font-size: 13px; color: #666;">Mob. No. ${customer?.contact || ""
    }</p>
          ${data.metaDetails?.gstNumber
      ? `<p style="margin: 5px 0; font-size: 12px; color: #666;">GST: ${data.metaDetails.gstNumber}</p>`
      : ""
    }
        </div>
        <div style="text-align: right;">
          <p style="margin: 5px 0; font-size: 14px;"><span style="color: #666;">Bill No:</span> <strong style="color: #2c3e50;">${data.transactionDetails?.billNo || 1
    }</strong></p>
          <p style="margin: 5px 0; font-size: 14px;"><span style="color: #666;">Date:</span> <strong style="color: #2c3e50;">${formatDate(
      data.date
    )}</strong></p>
          <p style="margin: 5px 0; font-size: 14px;"><span style="color: #666;">Seller:</span> <strong style="color: #2c3e50;">${data.metaDetails?.handledBy || ""
    }</strong></p>
          ${data.metaDetails?.panNumber
      ? `<p style="margin: 5px 0; font-size: 12px; color: #666;">PAN: ${data.metaDetails.panNumber}</p>`
      : ""
    }
        </div>
      </div>
      
      <!-- Items Table - Clean Design with Vertical Lines -->
      <div style="padding-bottom: 10px;">
        <table style="width: 100%; border-collapse: collapse; ">
          <thead>
  <tr style="background-color: #f8f9fa; border-bottom: 3px solid #333; border-top: 3px solid #333;">
    <th style="padding: 6px 6px; text-align: left; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 40%;">Particular</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">Net.Wt</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">CT.</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">Rate</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; border-right: 1px solid #333; width: 12%;">Making(%)</th>
    <th style="padding: 6px 6px; text-align: center; font-size: 12px; font-weight: 600; color: #2c3e50; text-transform: uppercase; letter-spacing: 0.5px; width: 22%;">Amount</th>
  </tr>
</thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
      </div>

      <!-- Amount in Words - Premium Highlight -->
      <div style="margin: 0 5px 5px 5px; background-color: #2c3e50; padding: 10px 20px; border-radius: 4px; color: white; border: 1px solid #d4af37;">
        <div style="display: flex; align-items: center;">
          <span style="font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #d4af37; margin-right: 15px;">Amount in Words</span>
          <span style="font-size: 14px; font-weight: 500;">${numberToWords(
      chargeableAmt
    ).toUpperCase()}</span>
        </div>
      </div>
      
      <!-- Payment Details Table -->
      <div style="padding: 0 5px 5px 5px;">
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #e0e0e0;">
          <thead>
            <tr style="background-color: #f8f9fa; border-bottom: 2px solid #9b8c7c;">
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Cash</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Online</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Cheque</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Old Gold</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Advance</th>
              <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50;">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr style="border-bottom: 1px solid #e0e0e0;">
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.cash || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.online || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.cheque || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.other || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 500; border-right: 1px solid #e0e0e0;">₹${payments?.advancePayment?.total || 0
    }</td>
              <td style="padding: 5px 10px; font-size: 12px; text-align: center; font-weight: 600; color: #ff0000;">₹${parseFloat(
      data?.summary?.pendingAmount || 0
    ).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      ${payments?.advancePayment?.enabled
      ? `
        <div style="padding: 0 5px 5px 5px;">
          <table style="width: 100%; border-collapse: collapse; border: 1px solid #e0e0e0;">
            <thead>
              <tr style="background-color: #f8f9fa; border-bottom: 2px solid #9b8c7c;">
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Cash</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Online</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Gold</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Adv Silver</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50; border-right: 1px solid #e0e0e0;">Other</th>
                <th style="padding: 5px 10px; font-size: 11px; text-transform: uppercase; color: #2c3e50;">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid #e0e0e0;">
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.cash || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.online || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.oldGold || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.oldSilver || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; border-right: 1px solid #e0e0e0; font-size: 12px;">₹${advanceData?.custom || 0
      }</td>
                <td style="padding: 5px 10px; text-align: center; font-weight: 600; color: #9b8c7c; font-size: 12px;">₹${payments?.advancePayment?.total || 0
      }</td>
              </tr>
            </tbody>
          </table>
        </div>
      `
      : ""
    }
      
      <!-- HSN & Bank Details with QR Code -->
      <div style="padding: 0 5px 5px 5px; display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; border: 1px solid #e0e0e0;">
          <p style="margin: 0 0 5px 0; font-size: 12px;"><span style="color: #666;">HSN/SAC:</span> <strong style="color: #2c3e50;">7113</strong></p>
          <p style="margin: 5px 0; font-size: 12px;"><span style="color: #666;">CGST:</span> <strong style="color: #2c3e50;">1.50%</strong> | <span style="color: #666;">SGST:</span> <strong style="color: #2c3e50;">1.50%</strong></p>

          <p style="margin: 8px 0 0 0; font-size: 11px; font-weight: 600; color: #2c3e50; text-transform: uppercase;">Terms & Conditions:</p>
          <ul style="margin: 0; padding-left: 15px; font-size: 10px; color: #666; line-height: 1.5;">
            <li>20% Deduction in KDM Ornaments.</li>
            <li>9% Deduction in Cash Transaction for 916 Hallmark Jewellery.</li>
          </ul>
        </div>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 4px; border: 1px solid #e0e0e0; display: flex; align-items: center; justify-content: space-between;">
          <div>
            <p style="margin: 0 0 5px 0; font-size: 12px; color: #666;">Scan to Pay</p>
            <p style="margin: 2px 0; font-size: 11px; color: #2c3e50;"><strong>HDFC BANK</strong></p>
            <p style="margin: 2px 0; font-size: 10px; color: #666;">A/C: 50200045267402</p>
            <p style="margin: 2px 0; font-size: 10px; color: #666;">IFSC: HDFC0001116</p>
          </div>
          <div style="text-align: center;">
            <img src="/assets/images/scanner.jpeg" style="width: 80px; height: 80px; border: 1px solid #e0e0e0; padding: 5px; background: white;">
            <p style="margin: 5px 0 0 0; font-size: 8px; color: #666;">Scan with any UPI app</p>
          </div>
        </div>
      </div>
      
      <!-- Declaration, Terms & Signatures with Social Media -->
      <div style="padding: 0 5px 5px 5px;">
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
          <div style="border: 1px solid #e0e0e0; padding: 15px; border-radius: 4px; background-color: #f8f9fa;">
            <p style="margin: 0 0 5px 0; font-size: 11px; color: #666;">Customer's Signature</p>
            <div style="height: 40px; border-bottom: 1px dashed #9b8c7c; margin-bottom: 10px;"></div>
          </div>
          <div style="text-align: right;">
            <div style="border: 1px solid #e0e0e0; padding: 16px; border-radius: 4px; background-color: #f8f9fa;">
              <p style="margin: 0; font-size: 11px; color: #666;">For ANKITA JEWELLERS</p>
              <div style="margin-top: 40px; border-top: 1px dashed #9b8c7c; padding-top: 10px;"></div>
            </div>
          </div>
        </div>
        
       <!-- Social Media Section with SVG Icons -->
<div style="margin-top: 10px; padding: 10px 15px; background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); border-radius: 4px; border: 1px solid #d4af37; text-align: center;">
  <div style="display: flex; justify-content: center; align-items: center; gap: 30px;">
    <a href="https://www.instagram.com/ankitajwls?utm_source=qr&igsh=MXQwNHNjcHo3dTZocQ%3D%3D" target="_blank" style="text-decoration: none; display: flex; align-items: center; gap: 8px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E4405F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
      </svg>
      <span style="font-size: 11px; font-weight: 500; color: #2c3e50;">@ANKITAJWLS</span>
    </a>
    <div style="width: 1px; height: 20px; background-color: #d4af37;"></div>
    <a href="https://wa.me/917304192892" target="_blank" style="text-decoration: none; display: flex; align-items: center; gap: 8px;">
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#25D366" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
      </svg>
      <span style="font-size: 11px; font-weight: 500; color: #2c3e50;">+91 73041 92892</span>
    </a>
  </div>
</div>
      
      <!-- Watermark -->
      <div style="position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.15; z-index: 9999; pointer-events: none;">
        <img src="/assets/images/logo.png" alt="Logo" style="width: 500px; height: 500px; object-fit: contain;">
      </div>
    </div>
  `;
};

// Generate bill HTML based on type
export const generateBillHTML = (billData) => {
  const { type, data, items, payments, customer, advanceData } = billData;

  return type === "gst"
    ? generateGSTBill(data, items, payments, customer, advanceData)
    : generateNonGSTBill(data, items, payments, customer, advanceData);
};

// Get CSS styles for printing
// Get CSS styles for printing
export const getBillStyles = () => `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');

/* Base styles for bill printing */
@media print {
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: white !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  body * {
    visibility: hidden !important;
  }
  
  #billContent, #billContentWithoutGst {
    position: absolute !important;
    left: 0 !important;
    top: 0 !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    box-shadow: none !important;
    background: white !important;
    visibility: visible !important;
    border: 2px solid #1a2a3a !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  #billContent *, #billContentWithoutGst * {
    visibility: visible !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  /* Preserve gradient backgrounds */
  [style*="background: linear-gradient"],
  [style*="background-image: linear-gradient"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  /* Enhance image quality */
  img {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    filter: contrast(1.05) brightness(1.02) !important;
    image-rendering: crisp-edges !important;
  }
  
  /* Improve text quality */
  h1, h2, h3, p, span, td, th {
    -webkit-font-smoothing: antialiased !important;
    text-rendering: optimizeLegibility !important;
  }
  
  /* Preserve border colors */
  td, th, div, table {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  table {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    width: 100% !important;
  }
  
  tr {
    page-break-inside: avoid !important;
    break-inside: avoid !important;
  }
  
  td, th {
    word-wrap: break-word !important;
    white-space: normal !important;
  }
  
  @page {
    size: A4 portrait;
    margin: 5mm;
  }
}

/* Screen styles - Enhanced for better rendering */
body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  background: #f5f5f5;
  margin: 0;
  padding: 20px;
}

/* Print optimization - Enhanced color preservation */
@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  /* Enhanced gradient rendering */
  [style*="background-color"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    box-shadow: none !important;
  }
  
  /* Improve dark colors */
  [style*="color: #2c3e50"],
  [style*="color: #1e2b37"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  
  /* Preserve gold accent */
  [style*="color: #d4af37"],
  [style*="border-color: #d4af37"] {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}

// Add this to your getBillStyles() function
@media print {
  /* Enhance contrast for glossy print */
  .bill-container {
    filter: contrast(1.05) saturate(1.02) !important;
  }
  
  /* Make gold accents pop */
  [style*="color: #d4af37"],
  [style*="color: #c4a747"] {
    color: #d4af37 !important;
    text-shadow: 0 0 1px rgba(0,0,0,0.1) !important;
  }
  
  /* Improve dark backgrounds */
  [style*="background: #1a2a3a"],
  [style*="background-color: #1a2a3a"],
  [style*="background: #2c3e50"],
  [style*="background-color: #2c3e50"] {
    background-color: #1a2a3a !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
`;

const billUtils = {
  formatDate,
  generateTransactionBillHTML,
  parseItemObject,
  getCarat,
  numberToWords,
  generateBillHTML,
  getBillStyles,
};

export default billUtils;
